import { Component, computed, OnDestroy, signal, inject } from '@angular/core';
import { OffcanvasBaseComponent } from '../../../../../shared/components/offcanvas/offcanvas-base/offcanvas-base.component';
import { OffcanvasHeaderBtnComponent } from '../../../../../shared/components/offcanvas/offcanvas-header-btn/offcanvas-header-btn.component';
import { CurrencyPipe, NgClass } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CardFundType, CardStatus, CardType } from '../../../../../_interfaces/cards.interface';
import { OffcanvasService } from '../../../../../_services/offcanvas-service/offcanvas.service';
import { ToastService } from '../../../../../_services/toast-service/toast.service';
import { CardsApiService } from '../../../../../_services/cards-service/cards-api/cards-api.service';
import { maskConfig } from '../../../../../shared/configs/configs';
import { NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select';
import { NgSelectCardOptionTemplateComponent } from '../../edit/shared/ng-select-card-dropdown-options/ng-select-card-option-template.component';
import { CardTopupsService } from '../../../../../_services/topup-service/card-topups.service';
import { immediateTopupSelectOptions } from '../../utils/utils';
import { EmptyIndicatorPipe } from '../../../../../shared/pipes/empty-indicator.pipe';
import { ImmediateTopUpLoadFormComponent } from './immediate-top-up-load-form/immediate-top-up-load-form.component';
import { ImmediateTopUpDeductFormComponent } from './immediate-top-up-deduct-form/immediate-top-up-deduct-form.component';
import { ImmediateTopUpSelectOption } from '../../../../../shared/interfaces/project-interfaces';

@Component({
  selector: 'app-immediate-top-up-create',
  imports: [
    OffcanvasBaseComponent,
    OffcanvasHeaderBtnComponent,
    ReactiveFormsModule,
    NgClass,
    NgOptionComponent,
    NgSelectCardOptionTemplateComponent,
    NgSelectComponent,
    FormsModule,
    CurrencyPipe,
    EmptyIndicatorPipe,
    ImmediateTopUpLoadFormComponent,
    ImmediateTopUpDeductFormComponent,
    EmptyIndicatorPipe,
  ],
  templateUrl: './immediate-top-up-create.component.html',
})
export class ImmediateTopUpCreateComponent implements OnDestroy {
  protected offcanvasService = inject(OffcanvasService);
  protected toastService = inject(ToastService);
  protected cardsApiService = inject(CardsApiService);
  protected cardTopupsService = inject(CardTopupsService);

  protected title = 'Immediate top-up';
  protected returnUrl = 'card-topups/immediate';
  protected readonly selectOptions = immediateTopupSelectOptions;
  protected readonly maskConfig = maskConfig;
  protected readonly CardFundType = CardFundType;
  protected cardBalance = signal<string>('');
  protected cardBalanceError = signal<boolean>(false);
  protected cardBalanceLoading = signal<boolean>(false);
  protected cardNumber = signal<string>('');
  readonly cardsList = computed(() =>
    this.cardTopupsService.cardsList().filter((card) => card.status !== CardStatus.New),
  );
  selectedCardId: number | null = null;

  getCardBalance() {
    if (!this.selectedCardId) return;
    this.cardBalanceLoading.set(true);
    this.cardBalanceError.set(false);
    this.cardBalance.set('');
    const cardType = this.getCardDetail(this.selectedCardId).type;
    this.cardNumber.set(this.getCardDetail(this.selectedCardId).number);

    if (cardType === CardType.Personal) {
      this.cardsApiService.getPersonalCardBalance(this.selectedCardId).subscribe({
        next: (balance) => this.cardBalance.set(balance.balance),
        error: () => {
          this.cardBalanceLoading.set(false);
          this.cardBalanceError.set(true);
        },
        complete: () => this.cardBalanceLoading.set(false),
      });
    } else if (cardType === CardType.Subscription) {
      this.cardsApiService.getSubscriptionCardBalance(this.selectedCardId).subscribe({
        next: (balance) => this.cardBalance.set(balance.balance),
        error: () => {
          this.cardBalanceLoading.set(false);
          this.cardBalanceError.set(true);
        },
        complete: () => this.cardBalanceLoading.set(false),
      });
    }
  }

  getCardDetail(cardId: number | null) {
    return {
      type: this.cardsList().find((card) => card.id === cardId)?.type,
      number: this.cardsList().find((card) => card.id === cardId)?.masked_number ?? '',
    };
  }

  onSelectOption(value: CardFundType) {
    for (const option of this.selectOptions) {
      option.checked = option.value === value;
    }
  }

  getSelectedOption(): ImmediateTopUpSelectOption | undefined {
    return this.selectOptions.find((option) => option.checked);
  }

  handleSubmitSuccess() {
    this.offcanvasService.close();
    this.offcanvasService.offCanvasNavigate(this.returnUrl);
    this.toastService.show(`The transfer was successful`, 'iconoir-check-circle');
  }

  ngOnDestroy(): void {
    this.offcanvasService.close();
  }
}
